import { forwardRef } from 'react';
import { Plus } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button, ButtonProps } from '../../../components/ui/button';

interface CreateButtonProps extends Omit<ButtonProps, 'children'> {
  /** The entity name to display (e.g., "Organization", "Contact") */
  children: React.ReactNode;
}

/**
 * Standardized create/add button — always renders a leading Plus icon followed
 * by the entity name. Use the SAME usage in page headers and empty-state CTAs so
 * the create affordance looks identical everywhere ("+ Organization").
 *
 * The Plus icon is the create affordance — never include verbs ("New", "Create",
 * "Add") in the label; pass the entity name only.
 *
 * @example
 * // Header and EmptyState — identical: icon + entity name
 * <CreateButton onClick={onCreate}>{t('common:entities.company')}</CreateButton>
 */
export const CreateButton = forwardRef<HTMLButtonElement, CreateButtonProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <Button ref={ref} className={cn(className)} {...props}>
        <Plus className="h-4 w-4" />
        {children}
      </Button>
    );
  }
);

CreateButton.displayName = 'CreateButton';
